import * as React from 'react';
import * as IoC from 'typescript-ioc';
import DatePicker from 'react-datepicker';
import './default-datepicker.scss';
import { isAfter, isBefore } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from '../../../infrastructure/utils/date-fns-overrides.utils';
import { DateTimeFormatter } from '../../../infrastructure/implementations/date-formatter';

interface IDateFormatInput {
    date: Date | null;
    value?: string | null;
    dateformat?: string;
}
interface ITimeFormatInput {
    date: Date | null;
    value?: string | null;
    timeformat?: string;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const DateFormatInput = React.forwardRef((props: IDateFormatInput, ref: React.Ref<HTMLInputElement>) => (
    <input
        ref={ref}
        type='text'
        {...props}
        value={props.date ? props.dateformat && dateTimeFormatter.dateToFormattedString(props.date, props.dateformat) ||
            dateTimeFormatter.dateToLocalString(props.date) : props.value}

    />
));

const TimeFormatInput = React.forwardRef((props: ITimeFormatInput, ref: React.Ref<HTMLInputElement>) => (
    <input
        type='text'
        ref={ref}
        {...props}
        value={props.date ? props.timeformat && dateTimeFormatter.dateToFormattedString(props.date, props.timeformat) ||
            dateTimeFormatter.dateToLocalString(props.date) : props.value}
    />
));

interface IDatePickerProps {
    className: string;
    showTimeSelect: boolean;
    selected: Date;
    dateformat: string;
    timeformat: string;
    timeIntervals: number;
    timeCaption: string;
    minDate: Date;
    maxDate: Date;
    disabledKeyboardNavigation: boolean;
    placeholderText: string | JSX.Element;
    timeClassName: (time: Date) => void;
    onChange: (date: Date) => void;
    name: string;
    strictParsing: boolean;
    timeZone?: string;
    id: string;
    customInput: React.FunctionComponent;
    showTimeSelectOnly: boolean;
    popperPlacement?: string;
    disabled?: boolean;
    isClearable?: boolean;
    highlightDates?: Date[];
    calendarClassName?: string;
    openToDate?: Date;
}

const defaults: Partial<IDatePickerProps> = {
    showTimeSelect: true,
    timeIntervals: 15,
    disabledKeyboardNavigation: true,
    timeformat: dateTimeFormatter.getLocalTimeFormat(),
};

const timeSlotClassName = (timeSlot: Date, selectedDate: Date, minDateTime?: Date, maxDateTime?: Date) => {
    let isDisabledTime: boolean;
    let dateInPicker = selectedDate ?? new Date();

    if (minDateTime && isBefore(dateInPicker, minDateTime)) {
        dateInPicker = minDateTime;
    } else if (maxDateTime && isBefore(maxDateTime, dateInPicker)) {
        dateInPicker = maxDateTime;
    }

    const currentDate = new Date(dateInPicker.getFullYear(), dateInPicker.getMonth(), dateInPicker.getDate(), timeSlot.getHours(), timeSlot.getMinutes());

    if (minDateTime) {
        if (isAfter(minDateTime, currentDate)) {
            isDisabledTime = true;
        }
    }

    if (maxDateTime) {
        if (isAfter(currentDate, maxDateTime)) {
            isDisabledTime = true;
        }
    }

    return isDisabledTime ? 'disabled-time' : '';
};


const DefaultDatePicker = (props: Partial<IDatePickerProps> = null) => {
    const finalProps = {
        timeClassName: time => timeSlotClassName(time, props.selected, props.minDate, props.maxDate),
        ...defaults,
        ...props,
    };

    if (finalProps.highlightDates) {
        finalProps.calendarClassName = 'oe-datepicker-highlighted';
    }

    // time, selected in datepicker, shall be converted to the local time with the respect of timezone
    if (!finalProps.showTimeSelect) {
        // must be unset to avoid time selection
        delete finalProps.showTimeSelect;
    }

    if (props.timeZone) {
        finalProps.minDate = props.minDate && utcToZonedTime(finalProps.minDate, props.timeZone);
        finalProps.maxDate = props.maxDate && utcToZonedTime(finalProps.maxDate, props.timeZone);
        finalProps.selected = props.selected && utcToZonedTime(finalProps.selected, props.timeZone);
        finalProps.openToDate = props.openToDate && utcToZonedTime(finalProps.openToDate, props.timeZone);

        const applyTimezoneShiftBeforeFeedingDateBack = (zonedDate: Date) => {
            if (zonedDate) {
                const utcDate = zonedTimeToUtc(zonedDate, props.timeZone);
                props.onChange(utcDate);
            } else {
                props.onChange(zonedDate); // null or undefined, just pass it over
            }
        };

        finalProps.onChange = props.onChange && applyTimezoneShiftBeforeFeedingDateBack;
    }

    let customInput = <DateFormatInput date={finalProps.selected} dateformat={finalProps.dateformat}/>;
    if (finalProps.showTimeSelectOnly) {
        customInput = <TimeFormatInput date={finalProps.selected} timeformat={finalProps.timeformat}/>;
    }

    return (
        <DatePicker {...finalProps} customInput={customInput} />
    );
};

export { DefaultDatePicker, DateFormatInput, TimeFormatInput };
